/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Table as BTable } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faFastBackward, faFastForward, faStepBackward, faStepForward, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import CategoryInfo from './CategoryInfo';

import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'

function ClientTable2({ rowSelection, setRowSelection, data, columns, keycol="key", onRowClick=null }) {
    const [globalFilter, setGlobalFilter] = useState<string>(""); /* eslint-disable-line */
    const theme: any = useTheme();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(), //provide a sorting row model
        globalFilterFn: 'includesString',
        enableGlobalFilter: true,
        enableMultiRowSelection: false, //only allow a single row to be selected at once
        onRowSelectionChange: setRowSelection,
        getRowId: (row : any) => row[keycol],
        state: {
            rowSelection,
        }
    })

    const selectedKey = Object.keys(rowSelection)[0];

    return (
        <Container>
            <input
                value={table.getState().globalFilter ?? ''}
                onChange={e => {
                    let newValue = String(e.target.value);
                    table.setGlobalFilter(newValue);
                }}
                className="p-2 font-lg shadow border border-block"
                placeholder="Search..."
                style={{
                    backgroundColor: theme.colors.bg2 || theme.colors.bg,
                    color: theme.colors.ctext,
                    borderColor: theme.colors.border || theme.colors.ctext,
                    width: '100%',
                    marginBottom: '0.5rem',
                }}
            />
            <BTable hover responsive size="sm">
            <thead>{table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>{headerGroup.headers.map(header => (
                    <th key={header.id} colSpan={header.colSpan}> 
                        <div onClick={header.column.getToggleSortingHandler()}>
                            {header.isPlaceholder ? null : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                            ) }
                            {header.column.getIsSorted() ? (
                                <SortIcon>
                                    <FontAwesomeIcon icon={header.column.getIsSorted() === 'desc' ? faCaretDown : faCaretUp} />
                                </SortIcon>
                            ) : null }
                        </div>
                    </th>
                ))}</tr>
            ))}</thead>
            <tbody>{table.getRowModel().rows.map(row => (
                <tr onClick={(e) => {
                    if (onRowClick) {
                        onRowClick(row.original);
                    } else {
                        row.getToggleSelectedHandler()?.(e);
                    }
                }} key={row.id}
                    className={row.getIsSelected() ? 'table-info' : undefined}
                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                    {row.getVisibleCells().map(cell => (
                        <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    ))}
                </tr>
            ))}</tbody>
            <tfoot>{table.getFooterGroups().map(footerGroup => (
                <tr key={footerGroup.id}>{footerGroup.headers.map(header => (
                    <th key={header.id} colSpan={header.colSpan}>{header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                    )}</th>
                ))}</tr>
            ))}</tfoot>
        </BTable>
    </Container >
  )
}

//Styles
const SortIcon = styled.span`
    margin-left: 5px;
`

export default ClientTable2;